/**
 * Tiny HTTP client wrapper.
 *
 * Conventions
 *  - All requests go to `${REACT_APP_BACKEND_URL}/api/...`.
 *  - Cookies travel by default (`credentials: "include"`) so the
 *    HttpOnly access cookie issued by `/api/auth/login` round-trips
 *    without any JS reading the token.
 *  - 401 responses surface as a thrown `ApiError` with `status: 401`
 *    so the AuthContext can react (clear local user, redirect to
 *    sign-in). 429 surfaces as `status: 429` so the sign-in page
 *    can render a "too many attempts" message.
 *  - Network/parse errors throw `ApiError(0, "Network error")`.
 */
const BASE = process.env.REACT_APP_BACKEND_URL || "";

export class ApiError extends Error {
  constructor(status, message, body) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

async function request(path, { method = "GET", body, headers } = {}) {
  const init = {
    method,
    credentials: "include",
    headers: { Accept: "application/json", ...(headers || {}) },
  };
  if (body !== undefined) {
    init.headers["Content-Type"] = "application/json";
    init.body = JSON.stringify(body);
  }
  let response;
  try {
    response = await fetch(`${BASE}${path}`, init);
  } catch (e) {
    throw new ApiError(0, "Network error", { cause: String(e) });
  }
  if (response.status === 204) return null;
  const text = await response.text();
  let payload = null;
  try {
    payload = text ? JSON.parse(text) : null;
  } catch {
    payload = { raw: text };
  }
  if (!response.ok) {
    const detail = payload?.detail ?? payload?.message ?? response.statusText;
    throw new ApiError(response.status, formatDetail(detail), payload);
  }
  return payload;
}

/**
 * Coerce a FastAPI error `detail` field into a single human-readable
 * string. FastAPI returns 422 validation errors as an array of
 * `{loc, msg, type, input}` objects — JS coerces that to
 * "[object Object]" via `new Error(arr)` which makes UI banners
 * useless. Centralise the formatting here so every caller gets a
 * string back via `err.message` and still has the structured array
 * available on `err.body.detail` if it needs it.
 */
function formatDetail(detail) {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d) => {
        if (typeof d === "string") return d;
        const field = Array.isArray(d?.loc) ? d.loc.slice(1).join(".") : "";
        const msg = d?.msg || JSON.stringify(d);
        return field ? `${field}: ${msg}` : msg;
      })
      .join("; ");
  }
  if (detail && typeof detail === "object") {
    return detail.msg || JSON.stringify(detail);
  }
  return String(detail ?? "Request failed");
}

export const api = {
  get:  (path)                  => request(path),
  post: (path, body, headers)   => request(path, { method: "POST",   body, headers }),
  patch:(path, body, headers)   => request(path, { method: "PATCH",  body, headers }),
  del:  (path, headers)         => request(path, { method: "DELETE", headers }),
};

export const USE_MOCK_DATA =
  (process.env.REACT_APP_USE_MOCK_DATA || "false").toLowerCase() === "true";
